import React, { useEffect, useState } from "react";
import { api, mediaSrcById } from "../api";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Search, Users, Briefcase, Loader2, Forward, Check, Send } from "lucide-react";

function initials(name) {
  return (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

/**
 * "Forward to..." picker — every conversation the user participates in
 * (direct contacts AND groups), with search. MULTI-SELECT: tap rows to toggle,
 * then send the copy to all selected conversations at once.
 */
export function ForwardDialog({ open, message, media, onClose }) {
  const { user, t } = useApp();
  const [convs, setConvs] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const mediaIds = (media || []).map((m) => m.id);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setComment("");
    setConvs(null);
    setSelected([]);
    api.get(`/conversations?user_id=${user.id}`)
      .then((r) => setConvs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setConvs([]));
  }, [open, user.id]);

  const filtered = (convs || []).filter(
    (c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase())
  );

  const toggle = (c) => {
    if (busy) return;
    setSelected((prev) => (prev.includes(c.id) ? prev.filter((id) => id !== c.id) : [...prev, c.id]));
  };

  const doForward = async () => {
    if (busy || selected.length === 0) return;
    setBusy(true);
    let ok = 0;
    for (const cid of selected) {
      try {
        if (mediaIds.length) {
          await api.post("/media/forward", { media_ids: mediaIds, to_conversation_id: cid, sender_id: user.id, comment: comment.trim() || null });
        } else {
          await api.post(`/messages/${message.id}/forward`, {
            to_conversation_id: cid,
            sender_id: user.id,
            comment: comment.trim() || null,
          });
        }
        ok += 1;
      } catch (e) { /* count failures below */ }
    }
    setBusy(false);
    if (ok === selected.length) {
      toast.success(selected.length > 1 ? `${t("message_forwarded")} (${ok})` : t("message_forwarded"));
      onClose();
    } else if (ok > 0) {
      toast.warning(`${t("message_forwarded")} (${ok}/${selected.length})`);
      onClose();
    } else {
      toast.error(t("something_wrong"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md z-[260]" data-testid="forward-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Forward className="h-5 w-5" /> {t("forward_to")}
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="forward-search"
            placeholder={t("search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>
        <div className="max-h-[42vh] overflow-y-auto -mx-2 px-2">
          {convs === null && (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          )}
          {convs !== null && filtered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">{t("no_results")}</p>
          )}
          {filtered.map((c) => {
            const isSel = selected.includes(c.id);
            return (
              <button
                key={c.id}
                data-testid={`forward-row-${c.id}`}
                onClick={() => toggle(c)}
                disabled={busy}
                aria-pressed={isSel}
                className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg transition-colors text-left disabled:opacity-60 ${isSel ? "bg-primary/10" : "hover:bg-muted active:bg-muted"}`}
              >
                <div className="relative shrink-0">
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold overflow-hidden">
                    {c.type === "group" ? (
                      c.avatar_media_id ? <img src={mediaSrcById(c.avatar_media_id)} alt="" className="h-full w-full object-cover" /> : <Users className="h-4 w-4" />
                    ) : c.other_user?.avatar_media_id ? (
                      <img src={mediaSrcById(c.other_user.avatar_media_id)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      initials(c.name)
                    )}
                  </div>
                  {isSel && (
                    <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center" data-testid={`forward-selected-${c.id}`}>
                      <Check className="h-3 w-3" />
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  {c.job_number && (
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Briefcase className="h-3 w-3" /> #{c.job_number}
                    </div>
                  )}
                </div>
                <span className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${isSel ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"}`}>
                  {isSel && <Check className="h-3.5 w-3.5" />}
                </span>
              </button>
            );
          })}
        </div>
        {selected.length > 0 && (
          <Textarea
            data-testid="forward-comment-input"
            placeholder={t("forward_comment_placeholder")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            maxLength={2000}
            className="resize-none text-sm"
          />
        )}
        {selected.length > 0 && (
          <Button
            data-testid="forward-send-button"
            onClick={doForward}
            disabled={busy}
            className="w-full h-11 rounded-xl"
          >
            {busy
              ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
              : <Send className="h-4 w-4 mr-2" />}
            {t("send")} ({selected.length})
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
